import type { CardCollection } from '../shared/collections';
import { createCardPreview } from './card-preview';
import { describePackPreview, type PackPreview } from './pack-preview-data';
import { getLocale, t } from './i18n';
import { escape, icon } from './ui';
import './pack-preview.css';

/** A direct route from every pack tile to its cards and complete artwork list. */
export function createPackPreview() {
  const dialog = document.createElement('dialog');
  dialog.className = 'card-preview-dialog pack-preview-dialog';
  dialog.setAttribute('aria-labelledby', 'pack-preview-title');
  const tr = (key: string) => escape(t(key));
  dialog.innerHTML = `<div class="card-preview-shell"><header class="card-preview-heading"><div><h2 id="pack-preview-title"></h2><span class="card-preview-details"></span></div><button type="button" class="icon-button" data-pack-close aria-label="${tr('Fermer')}">${icon('close',28)}</button></header><div class="card-preview-stage"></div><section class="pack-preview-images" aria-label="${tr('Toutes les images')}" hidden></section><footer><div class="pack-card-navigation"><div class="card-preview-controls"><button type="button" class="icon-button" data-card-previous aria-label="${tr('Carte précédente')}">${icon('back',26)}</button><span class="card-preview-counter" role="status" aria-live="polite"></span><button type="button" class="icon-button" data-card-next aria-label="${tr('Carte suivante')}">${icon('arrow',26)}</button></div><p class="card-preview-hint">${tr('Glisse pour changer de carte')}</p></div><p class="pack-preview-draft" hidden>${tr('Complète le jeu pour voir les cartes.')}</p><button type="button" class="pack-preview-toggle" aria-pressed="false">${icon('stack',20)}<span>${tr('Toutes les images')}</span></button></footer></div>`;
  document.body.append(dialog);
  const title = dialog.querySelector<HTMLElement>('h2')!;
  const gallery = dialog.querySelector<HTMLElement>('.pack-preview-images')!;
  const stage = dialog.querySelector<HTMLElement>('.card-preview-stage')!;
  const navigation = dialog.querySelector<HTMLElement>('.pack-card-navigation')!;
  const draft = dialog.querySelector<HTMLElement>('.pack-preview-draft')!;
  const toggle = dialog.querySelector<HTMLButtonElement>('.pack-preview-toggle')!;
  const details = dialog.querySelector<HTMLElement>('.card-preview-details')!;
  const cardPreview = createCardPreview(dialog, () => {});
  const sides = dialog.querySelector<HTMLElement>('.card-preview-sides')!;
  let pack: PackPreview | undefined;
  let galleryVisible = false;
  let returnFocus: HTMLElement | null = null;
  let cardDetails = '';

  function showImages(value: boolean) {
    galleryVisible = value;
    dialog.classList.toggle('is-gallery', value);
    gallery.hidden = !value;
    stage.hidden = sides.hidden = navigation.hidden = value;
    draft.hidden = !!pack?.cards.length;
    toggle.hidden = !pack?.cards.length;
    toggle.setAttribute('aria-pressed', String(value));
    toggle.querySelector('span')!.textContent = t(value ? 'Voir les cartes' : 'Toutes les images');
    details.textContent = value ? t('{count} images', { count: pack?.symbols.length ?? 0 }) : cardDetails;
  }
  toggle.addEventListener('click', () => {
    if (!galleryVisible) cardDetails = details.textContent ?? '';
    showImages(!galleryVisible);
  });
  dialog.addEventListener('keydown', event => {
    if (galleryVisible && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) event.stopImmediatePropagation();
  }, true);
  dialog.querySelector('[data-pack-close]')!.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => {
    // Release any local base64 image references once the reader is closed.
    gallery.replaceChildren(); stage.replaceChildren(); pack = undefined;
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  });

  return {
    open(selected: CardCollection | 'classic' | null) {
      pack = describePackPreview(selected, getLocale());
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      title.textContent = pack.name;
      gallery.innerHTML = pack.symbols.map(symbol => `<figure class="pack-preview-symbol"><img src="${escape(symbol.image)}" alt="" loading="lazy"><figcaption>${escape(symbol.name)}</figcaption></figure>`).join('')
        || `<p class="pack-preview-empty">${tr('Ce jeu ne contient pas encore d’images.')}</p>`;
      // Render only the first card's symbols now; the rest load as the reader browses.
      if (pack.cards.length) {
        cardPreview.open(pack.cards, 0, 85, pack.collection);
        cardDetails = details.textContent ?? '';
      }
      showImages(!pack.cards.length);
      if (!dialog.open) dialog.showModal();
      dialog.querySelector<HTMLButtonElement>('[data-pack-close]')!.focus();
    },
    close() { dialog.close(); },
    destroy() { dialog.close(); dialog.remove(); },
  };
}
